import { FormEvent, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { getSignupProductByKey } from '../../../config/SignupProductConfig';
import { isValidEmailAddress } from '../../../services/Utilities';
import { clearStoredSignupFlow, createSignupHandoffId, getSignupReturnPath, readSignupReturnPath, readStoredProviderSignupFlow, readStoredSignupFlow, writeStoredSignupFlow, type SignupMethod } from '../../../services/SignupFlowStorage';
import {
  completeClassLinkSignup,
  completeCanvasSignup,
  completeCleverSignup,
  completeSchoologySignup,
  completeEmailSignup,
  getClassLinkSignupContext,
  getCanvasSignupContext,
  getCleverSignupContext,
  getSchoologySignupContext,
  prepareSignupContext,
  signInExistingAccount,
  startClassLinkSignup,
  startCleverSignup,
  startGoogleSignup,
  submitEmailSignup,
  type PreparedSignupContext,
  type SignupContextRequest,
} from '../../../services/SignupService';
import { SignupAccountMethods, type SignupProvider } from '../../../components/signup/SignupAccountMethods/SignupAccountMethods';
import { StepperComponent } from '../../../components/core/StepperComponent/StepperComponent';
import { SignupHeader } from '../../../components/signup/SignupHeader/SignupHeader';
import { SignupProgramStep } from '../../../components/signup/SignupProgramStep/SignupProgramStep';
import { SignupSchoolFinder, createSchoolFinderState } from '../../../components/signup/SignupSchoolFinder/SignupSchoolFinder';
import { SignupSubscriptionToken } from '../../../components/signup/SignupSubscriptionToken/SignupSubscriptionToken';
import { SignupSummaryCard } from '../../../components/signup/SignupSummaryCard/SignupSummaryCard';
import { SignupAccountExists } from '../../../components/signup/SignupAccountExists/SignupAccountExists';
import { SignupCheckEmail } from '../../../components/signup/SignupCheckEmail/SignupCheckEmail';
import { SignupCreateAccount } from '../../../components/signup/SignupCreateAccount/SignupCreateAccount';
import { SignupDomainError } from '../../../components/signup/SignupDomainError/SignupDomainError';
import { SignupError } from '../../../components/signup/SignupError/SignupError';
import { SignupDistrictAdmin } from '../../../components/signup/SignupDistrictAdmin/SignupDistrictAdmin';
import { consentErrorMessage, emailErrorMessage, getCanvasSignupErrorMessage, getClassLinkSignupErrorMessage, getCleverSignupErrorMessage, getSchoologySignupErrorMessage, getSignupErrorMessage, type SignupActionError } from '../../../components/signup/SignupMessages';
import { getSignupRoute, type SignupResult } from './SignupRoute';
import { signupPageStyles } from './SignupPage.styles';

type ActionStatus = 'idle' | 'loading' | 'error';
type SignupStep = 1 | 2 | 3;

export function SignupPage() {
  const route = getSignupRoute(window.location.pathname, window.location.search);
  const [districtRecovery] = useState(() => {
    if (route.result !== 'domain-error' && route.result !== 'account-exists') {
      return null;
    }
    const flow = readStoredSignupFlow(route.handoffId);
    return flow?.districtAdminProductKey ? flow : null;
  });
  if (route.districtAdminProductKey) {
    return <SignupDistrictAdmin productKey={route.districtAdminProductKey} />;
  }
  if (districtRecovery?.districtAdminProductKey) {
    return <SignupDistrictAdmin productKey={districtRecovery.districtAdminProductKey} restoredFlow={districtRecovery} result={route.result === 'account-exists' ? 'account-exists' : 'domain-error'} isArchived={route.accountStatus === 'archived'} />;
  }
  return <StandardSignupPage />;
}

function StandardSignupPage() {
  const theme = useTheme();
  const route = getSignupRoute(window.location.pathname, window.location.search);
  const initialSignupResult = route.result;
  const [restoredFlow] = useState(() => {
    if (route.classLinkState || route.classLinkErrorCode) {
      return readStoredProviderSignupFlow('classlink');
    }
    if (route.cleverState || route.cleverErrorCode) {
      return readStoredProviderSignupFlow('clever');
    }
    if (route.canvasState) {
      return readStoredProviderSignupFlow('canvas');
    }
    // A typed Canvas failure invalidates its continuation and must not revive the saved School step.
    if (route.canvasErrorCode) {
      return null;
    }
    if (route.schoologyState || route.schoologyErrorCode) {
      return readStoredProviderSignupFlow('schoology');
    }
    if (initialSignupResult === 'domain-error' || initialSignupResult === 'account-exists') {
      const matchedFlow = readStoredSignupFlow(route.handoffId);
      if (matchedFlow) {
        return matchedFlow;
      }
      return initialSignupResult === 'domain-error' && !route.handoffId
        ? readStoredProviderSignupFlow('classlink') ||
          readStoredProviderSignupFlow('clever') ||
          readStoredProviderSignupFlow('canvas') ||
          readStoredProviderSignupFlow('schoology')
        : null;
    }
    return null;
  });
  const [signupResult, setSignupResult] = useState<SignupResult>(initialSignupResult);
  const requestedProductKey = restoredFlow?.requestedProductKey || (signupResult === 'form' ? route.productKey : null);
  const requestedProgram = signupResult === 'form' ? route.program : '';
  const [signupReturnPath] = useState(() => restoredFlow?.signupReturnPath ||
    (signupResult === 'form' ? getSignupReturnPath(window.location.search) : readSignupReturnPath(route.handoffId)));
  const [activeStep, setActiveStep] = useState<SignupStep>(
    restoredFlow?.originProvider === 'schoology' || restoredFlow?.originProvider === 'canvas'
      ? initialSignupResult === 'domain-error' && restoredFlow.preparedTokenContext ? 1 : 2
      : restoredFlow ? 3 : requestedProgram ? 2 : 1,
  );
  const [subject, setSubject] = useState(restoredFlow?.subject || requestedProgram);
  // Keep finder state across step unmounts and reuse it when saving signup recovery details.
  const [schoolFinder, setSchoolFinder] = useState(() => createSchoolFinderState(restoredFlow));
  const {zipCode, campuses, campusId, isMissingSchool, stateOptions, stateCode} = schoolFinder;
  const [emailAddress, setEmailAddress] = useState(restoredFlow?.emailAddress || '');
  const [subscriptionToken, setSubscriptionToken] = useState(restoredFlow?.subscriptionToken || '');
  const [classLinkState, setClassLinkState] = useState(route.classLinkState);
  const [cleverState, setCleverState] = useState(route.cleverState);
  const [canvasState, setCanvasState] = useState(
    route.canvasState ||
    (initialSignupResult === 'domain-error' && restoredFlow?.originProvider === 'canvas'
      ? restoredFlow.canvasState || ''
      : ''),
  );
  const [schoologyState, setSchoologyState] = useState(
    route.schoologyState ||
    (initialSignupResult === 'domain-error' && restoredFlow?.originProvider === 'schoology'
      ? restoredFlow.schoologyState || ''
      : ''),
  );
  const [providerMappingStatus, setProviderMappingStatus] = useState<'exact' | 'missing' | 'ambiguous' | null>(null);
  const [providerMappedCampusId, setProviderMappedCampusId] = useState('');
  const [preparedTokenContext, setPreparedTokenContext] = useState<PreparedSignupContext | null>(restoredFlow?.preparedTokenContext || null);
  const [isTokenVisible, setIsTokenVisible] = useState(Boolean(requestedProductKey || restoredFlow?.preparedTokenContext));
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(restoredFlow ? restoredFlow.hasAcceptedTerms !== false : false);
  const [showConsentError, setShowConsentError] = useState(false);
  const [actionStatus, setActionStatus] = useState<ActionStatus>(route.classLinkErrorCode || route.cleverErrorCode || route.canvasErrorCode || route.schoologyErrorCode ? 'error' : 'idle');
  const [actionError, setActionError] = useState<SignupActionError | null>(() => {
    const message = getClassLinkSignupErrorMessage(route.classLinkErrorCode) ||
      getCleverSignupErrorMessage(route.cleverErrorCode) ||
      getCanvasSignupErrorMessage(route.canvasErrorCode) ||
      getSchoologySignupErrorMessage(route.schoologyErrorCode);
    return message ? {reason: 'request', message} : null;
  });
  const [existingAccountPassword, setExistingAccountPassword] = useState('');
  const presentationProductKey = (preparedTokenContext?.viewMode === 'product'
    ? preparedTokenContext.productKey
    : null) || requestedProductKey;
  const isProductView = Boolean(presentationProductKey);
  const restoredOriginProvider = restoredFlow?.originProvider === 'canvas' && !canvasState
    ? undefined
    : restoredFlow?.originProvider;
  const originProvider: SignupProvider | undefined = route.classLinkState || route.classLinkErrorCode
    ? 'classlink'
    : route.cleverState || route.cleverErrorCode
      ? 'clever'
      : canvasState
        ? 'canvas'
        : route.schoologyState || route.schoologyErrorCode ? 'schoology' : restoredOriginProvider;
  const continuationProvider = classLinkState
    ? 'classlink'
    : cleverState ? 'clever' : canvasState ? 'canvas' : schoologyState ? 'schoology' : null;
  const continuationState = classLinkState || cleverState || canvasState || schoologyState;

  useEffect(() => {
    // Clear after initialization so StrictMode can repeat initializer reads; typed Canvas errors still discard their stale snapshot.
    if (restoredFlow || route.canvasErrorCode) {
      clearStoredSignupFlow();
    }
  }, [restoredFlow, route.canvasErrorCode]);

  useEffect(() => {
    if (signupResult !== 'form') {
      return;
    }

    const {subscriptionToken: token, searchWithoutToken} = getSignupRoute(window.location.pathname, window.location.search);
    if (!token) {
      return;
    }

    window.history.replaceState({}, '', `${window.location.pathname}${searchWithoutToken ? `?${searchWithoutToken}` : ''}${window.location.hash}`);

    setSubscriptionToken(token);
    setIsTokenVisible(true);
    void applySubscriptionToken(token);
  }, [signupResult]);

  useEffect(() => {
    if (!continuationProvider || !continuationState || signupResult !== 'form') {
      return;
    }

    let isCurrent = true;
    setActionStatus('loading');
    setActionError(null);

    const getContext = continuationProvider === 'classlink'
      ? getClassLinkSignupContext
      : continuationProvider === 'clever'
        ? getCleverSignupContext
        : continuationProvider === 'canvas' ? getCanvasSignupContext : getSchoologySignupContext;
    void getContext(continuationState).then((context) => {
      if (!isCurrent) {
        return;
      }
      setProviderMappingStatus(context.mappingStatus);
      const school = context.school;
      setProviderMappedCampusId(context.mappingStatus === 'exact' && school ? school.id : '');
      if (school) {
        setSchoolFinder((current) => ({
          ...current,
          zipCode: school.zip || current.zipCode,
          campuses: [school],
          campusId: school.id,
          isMissingSchool: false,
          stateCode: '',
        }));
      }
      setActionStatus('idle');
    }).catch((error) => {
      if (!isCurrent) {
        return;
      }
      if (continuationProvider === 'classlink') {
        setClassLinkState('');
      } else if (continuationProvider === 'clever') {
        setCleverState('');
      } else if (continuationProvider === 'canvas') {
        setCanvasState('');
      } else {
        setSchoologyState('');
      }
      setActionError({reason: 'request', message: getSignupErrorMessage(error)});
      setActionStatus('error');
    });

    return () => {
      isCurrent = false;
    };
  }, [continuationProvider, continuationState, signupResult]);

  useEffect(() => {
    if (signupResult === 'check-email' || signupResult === 'create-account') {
      clearStoredSignupFlow();
    }
  }, [signupResult]);

  function requireConsent() {
    if (hasAcceptedTerms) {
      return true;
    }
    setShowConsentError(true);
    setActionError({reason: 'consent', message: consentErrorMessage});
    setActionStatus('error');
    return false;
  }

  function buildContextRequest(): SignupContextRequest | null {
    if (preparedTokenContext && subscriptionToken.trim()) {
      return {selection: {type: 'token', token: subscriptionToken.trim()}};
    }
    if (!subject) {
      setActionError({reason: 'selection', message: 'Select a program to continue.'});
      return null;
    }
    if (isMissingSchool) {
      if (!stateCode) {
        setActionError({reason: 'selection', message: 'Select your state to continue.'});
        return null;
      }
      return {subject, selection: {type: 'school_not_listed', stateCode}};
    }
    if (!campusId) {
      setActionError({reason: 'selection', message: 'Select your school or choose “Can’t find your school?” to continue.'});
      return null;
    }
    return {subject, selection: {type: 'school', schoolId: campusId}};
  }

  async function prepareCurrentContext(provider: SignupMethod | null = null, providerState = '') {
    const baseRequest = buildContextRequest();
    if (!baseRequest) {
      return false;
    }
    const request = provider === 'classlink' && providerState
      ? {...baseRequest, classLinkState: providerState}
      : provider === 'clever' && providerState
        ? {...baseRequest, cleverState: providerState}
        : provider === 'schoology' && providerState
          ? {...baseRequest, schoologyState: providerState}
          : provider === 'canvas' && providerState
            ? {...baseRequest, canvasState: providerState}
        : baseRequest;
    await prepareSignupContext(request);
    return true;
  }

  function storeStandardSignupFlow(
    signupMethod: SignupMethod,
    tokenRecovery?: {subscriptionToken: string; preparedTokenContext: PreparedSignupContext},
  ) {
    const recoveryTokenContext = tokenRecovery?.preparedTokenContext || preparedTokenContext;
    const recoveryToken = tokenRecovery?.subscriptionToken || subscriptionToken;
    const handoffId = createSignupHandoffId();
    writeStoredSignupFlow({
      handoffId,
      subject,
      zipCode,
      campuses,
      campusId,
      isMissingSchool,
      stateOptions,
      stateCode,
      emailAddress: emailAddress.trim(),
      signupMethod,
      originProvider: originProvider ||
        (signupMethod === 'classlink' || signupMethod === 'clever' || signupMethod === 'canvas' || signupMethod === 'schoology'
          ? signupMethod
          : undefined),
      canvasState: signupMethod === 'canvas' ? canvasState : undefined,
      schoologyState: signupMethod === 'schoology' ? schoologyState : undefined,
      subscriptionToken: recoveryTokenContext ? recoveryToken : undefined,
      preparedTokenContext: recoveryTokenContext || undefined,
      requestedProductKey: requestedProductKey || undefined,
      signupReturnPath,
      hasAcceptedTerms,
    });
    return handoffId;
  }

  async function startAccountSignup(signupMethod: SignupMethod) {
    if (!requireConsent()) {
      return;
    }
    if (signupMethod === 'email' && !isValidEmailAddress(emailAddress)) {
      setActionError({reason: 'email', message: emailErrorMessage});
      setActionStatus('error');
      return;
    }
    setActionStatus('loading');
    setActionError(null);

    try {
      const currentProviderState = signupMethod === continuationProvider ? continuationState : '';
      if (!await prepareCurrentContext(signupMethod, currentProviderState)) {
        setActionStatus('error');
        return;
      }
      const handoffId = storeStandardSignupFlow(signupMethod);
      if (signupMethod === 'classlink') {
        if (classLinkState) {
          completeClassLinkSignup(classLinkState);
        } else {
          startClassLinkSignup();
        }
      } else if (signupMethod === 'clever') {
        if (cleverState) {
          completeCleverSignup(cleverState);
        } else {
          startCleverSignup();
        }
      } else if (signupMethod === 'schoology') {
        completeSchoologySignup(schoologyState);
      } else if (signupMethod === 'canvas') {
        completeCanvasSignup(canvasState);
      } else if (signupMethod === 'google') {
        await startGoogleSignup(handoffId);
      } else {
        submitEmailSignup(emailAddress.trim(), handoffId);
      }
    } catch (error) {
      setActionError({reason: 'request', message: getSignupErrorMessage(error)});
      setActionStatus('error');
    }
  }

  async function completeProviderFromSchool() {
    const provider = originProvider === 'canvas' ? 'canvas' : 'schoology';
    const providerState = provider === 'canvas' ? canvasState : schoologyState;
    if (!providerState) {
      setActionError({
        reason: 'request',
        message: provider === 'canvas'
          ? getCanvasSignupErrorMessage('canvas_continuation_expired')
          : getSchoologySignupErrorMessage('schoology_continuation_expired'),
      });
      setActionStatus('error');
      return;
    }

    setActionStatus('loading');
    setActionError(null);
    try {
      if (!await prepareCurrentContext(provider, providerState)) {
        setActionStatus('error');
        return;
      }
      storeStandardSignupFlow(provider);
      if (provider === 'canvas') {
        completeCanvasSignup(providerState);
      } else {
        completeSchoologySignup(providerState);
      }
    } catch (error) {
      setActionError({reason: 'request', message: getSignupErrorMessage(error)});
      setActionStatus('error');
    }
  }

  async function handleProviderSignUp(provider: SignupProvider) {
    await startAccountSignup(provider);
  }

  async function handleEmailSignUp(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await startAccountSignup('email');
  }

  async function applySubscriptionToken(token: string) {
    setActionStatus('loading');
    setActionError(null);
    setPreparedTokenContext(null);

    try {
      const directProvider = originProvider === 'canvas' ? 'canvas' : 'schoology';
      const directProviderState = directProvider === 'canvas' ? canvasState : schoologyState;
      const isDirectProvider = originProvider === 'canvas' || originProvider === 'schoology';
      if (isDirectProvider && !directProviderState) {
        setActionError({
          reason: 'request',
          message: directProvider === 'canvas'
            ? getCanvasSignupErrorMessage('canvas_continuation_expired')
            : getSchoologySignupErrorMessage('schoology_continuation_expired'),
        });
        setActionStatus('error');
        return;
      }
      const context = await prepareSignupContext(isDirectProvider
        ? directProvider === 'canvas'
          ? {selection: {type: 'token', token}, canvasState: directProviderState}
          : {selection: {type: 'token', token}, schoologyState: directProviderState}
        : {selection: {type: 'token', token}});
      if (requestedProductKey && context.productKey !== requestedProductKey) {
        setActionError({reason: 'subscription', message: `This subscription token is not valid for ${getSignupProductByKey(requestedProductKey).label}.`});
        setActionStatus('error');
        return;
      }
      setPreparedTokenContext(context);
      setSubject('');
      setSchoolFinder((current) => ({...current, campusId: '', isMissingSchool: false}));
      if (isDirectProvider) {
        storeStandardSignupFlow(directProvider, {
          subscriptionToken: token,
          preparedTokenContext: context,
        });
        if (directProvider === 'canvas') {
          completeCanvasSignup(directProviderState);
        } else {
          completeSchoologySignup(directProviderState);
        }
        return;
      }
      setActionStatus('idle');
      if (!requestedProductKey) {
        setActiveStep(3);
      }
    } catch (error) {
      setActionError({reason: 'request', message: getSignupErrorMessage(error)});
      setActionStatus('error');
    }
  }

  function handleTermsChange(checked: boolean) {
    setHasAcceptedTerms(checked);
    if (checked) {
      setShowConsentError(false);
      if (actionError?.reason === 'consent') {
        setActionError(null);
        setActionStatus('idle');
      }
    }
  }

  function resetSubscriptionToken() {
    setPreparedTokenContext(null);
    setSubscriptionToken('');
    setActionError(null);
    if (!isDirectProviderContinuation && signupResult !== 'form') {
      leaveInlineResult(1);
    }
    if (!requestedProductKey) {
      setActiveStep(1);
    }
  }

  async function handleExistingAccountSignIn(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setActionStatus('loading');
    setActionError(null);

    try {
      await signInExistingAccount(emailAddress.trim(), existingAccountPassword);
      clearStoredSignupFlow();
    } catch {
      setActionError({reason: 'sign-in', message: 'We couldn’t sign you in with that password. Check your password and try again.'});
      setActionStatus('error');
    }
  }

  function leaveInlineResult(nextStep: SignupStep) {
    if (signupResult !== 'form') {
      window.history.replaceState({}, '', signupReturnPath);
      setSignupResult('form');
    }
    setActionError(null);
    setActionStatus('idle');
    setActiveStep(nextStep);
  }

  function showSignupMethods() {
    setEmailAddress('');
    window.history.replaceState({}, '', signupReturnPath);
    leaveInlineResult(3);
  }

  const isWorking = actionStatus === 'loading';
  const hasSignupContext = Boolean((preparedTokenContext && subscriptionToken.trim()) || (subject && (isMissingSchool ? stateCode : campusId)));
  const isDirectProviderContinuation = originProvider === 'schoology' || originProvider === 'canvas';
  const directProviderState = originProvider === 'canvas' ? canvasState : schoologyState;
  const directProviderName = originProvider === 'canvas' ? 'Canvas' : 'Schoology';
  const canAttemptSignup = hasSignupContext && !isWorking &&
    (!isDirectProviderContinuation || Boolean(directProviderState));
  const productPresentation = presentationProductKey ? getSignupProductByKey(presentationProductKey) : null;
  const pageTitle = productPresentation ? `Create your ${productPresentation.label} account` : 'Create your Exploros account';
  const selectedCampus = campuses.find((campus) => campus.id === campusId) || null;
  const selectedState = stateOptions.find((state) => state.code === stateCode) || null;
  const schoolName = preparedTokenContext?.schoolName || (isMissingSchool ? 'No school listed' : selectedCampus?.name || '');
  const schoolDetail = isMissingSchool ? selectedState?.label || stateCode : selectedCampus ? [selectedCampus.city, selectedCampus.state].filter(Boolean).join(', ') : '';
  const programName = preparedTokenContext?.planName || productPresentation?.label || subject;
  const canVisitSchool = Boolean(subject) && !preparedTokenContext;
  const canVisitAccount = hasSignupContext;
  const isInlineAccountResult = Boolean(restoredFlow && (signupResult === 'domain-error' || signupResult === 'account-exists'));
  const isArchivedAccount = signupResult === 'account-exists' && route.accountStatus === 'archived';
  const restoredSignupMethod = restoredFlow?.signupMethod || (restoredFlow?.emailAddress ? 'email' : 'google');
  const isTokenBackedProviderDomainError = signupResult === 'domain-error' &&
    isDirectProviderContinuation && Boolean(preparedTokenContext && subscriptionToken.trim());
  const providerDomainError = signupResult === 'domain-error' && isDirectProviderContinuation
    ? isTokenBackedProviderDomainError
      ? `The email associated with this ${directProviderName} account doesn’t match the subscription for ${schoolName || 'the selected school'}. Use a different subscription token or a ${directProviderName} account with your school-issued email.`
      : `The email associated with this ${directProviderName} account doesn’t match the information we have for ${schoolName || 'the selected school'}. Choose another school or use a ${directProviderName} account with your school-issued email.`
    : '';
  const schoolStepActionError = isDirectProviderContinuation
    ? actionError?.message || (isWorking ? '' : providerDomainError)
    : '';

  if (signupResult !== 'form' && !isInlineAccountResult) {
    switch (signupResult) {
      case 'check-email':
        return <SignupCheckEmail />;
      case 'account-exists':
        return <SignupAccountExists isArchived={isArchivedAccount} signupReturnPath={signupReturnPath} />;
      case 'domain-error':
        return <SignupDomainError signupReturnPath={signupReturnPath} errorCode={route.domainErrorCode} />;
      case 'create-account':
        return <SignupCreateAccount verificationHash={route.verificationHash} onComplete={completeEmailSignup} />;
      case 'error':
        return <SignupError errorCode={route.errorCode} />;
    }
  }

  const subscriptionTokenControls = (
    <SignupSubscriptionToken
      token={subscriptionToken}
      isWorking={isWorking}
      isProductView={isProductView}
      showTokenReplacement={signupResult === 'domain-error'}
      confirmedSubscription={preparedTokenContext ? {
        productLabel: productPresentation?.label,
        schoolName: preparedTokenContext.schoolName,
        planName: preparedTokenContext.planName,
      } : undefined}
      onReset={resetSubscriptionToken}
      onTokenChange={setSubscriptionToken}
      onUseToken={applySubscriptionToken}
    />
  );

  const providerTokenRecovery = (
    <Box component="section" aria-labelledby="provider-token-recovery-heading" sx={signupPageStyles.tokenRecovery()}>
      <Typography id="provider-token-recovery-heading" component="h2" sx={signupPageStyles.tokenRecoveryHeading()}>We couldn’t verify this {directProviderName} account</Typography>
      <Typography role="alert" sx={signupPageStyles.tokenRecoveryError(theme)}>{providerDomainError}</Typography>
      {subscriptionTokenControls}
    </Box>
  );

  const accountMethods = (
    <SignupAccountMethods
      isProductView={isProductView}
      requiredProvider={originProvider}
      canAttemptSignup={canAttemptSignup}
      isWorking={isWorking}
      emailAddress={emailAddress}
      hasAcceptedTerms={hasAcceptedTerms}
      showConsentError={showConsentError}
      showEmailError={actionError?.reason === 'email'}
      actionError={actionError?.message || ''}
      onEmailChange={setEmailAddress}
      onTermsChange={handleTermsChange}
      onProviderSignUp={handleProviderSignUp}
    />
  );

  const accountContent = signupResult === 'domain-error' ? (
    <SignupDomainError errorCode={route.domainErrorCode} inline={{
      provider: restoredSignupMethod,
      schoolName,
      emailAddress,
      isWorking,
      failedEmailAddress: restoredFlow?.emailAddress || '',
      error: actionError?.message || '',
      onEmailChange: setEmailAddress,
      onUseDifferentMethod: showSignupMethods,
    }} />
  ) : signupResult === 'account-exists' ? (
    <SignupAccountExists isArchived={isArchivedAccount} inline={{
      programName,
      schoolName,
      password: existingAccountPassword,
      isWorking,
      error: actionError?.message || '',
      onPasswordChange: setExistingAccountPassword,
      onUseDifferentAccount: showSignupMethods,
    }} />
  ) : accountMethods;

  return (
    <Box component="main" sx={signupPageStyles.page(theme)}>
      <Box sx={signupPageStyles.content(theme)}>
        <SignupHeader
          title={pageTitle}
          description="Sign up as a teacher or administrator."
          logoSrc={productPresentation?.logoAssetUrl}
          logoAlt={productPresentation?.label}
          isProductLogo={Boolean(productPresentation)}
          isCentered
        />

        <Box component="form" onSubmit={signupResult === 'account-exists' ? handleExistingAccountSignIn : handleEmailSignUp} sx={signupPageStyles.form()}>
          {isProductView ? (
            <Box sx={signupPageStyles.productForm()}>
              {isTokenBackedProviderDomainError ? providerTokenRecovery : (
                <>
                  {subscriptionTokenControls}
                  {accountContent}
                </>
              )}
            </Box>
          ) : (
            <Box sx={signupPageStyles.stagedForm()}>
              <StepperComponent<SignupStep>
                ariaLabel="Signup progress"
                activeStep={activeStep}
                steps={isDirectProviderContinuation
                  ? [
                      {id: 1, label: 'Program', completed: activeStep > 1, disabled: isWorking},
                      {id: 2, label: 'School', disabled: isWorking || !canVisitSchool},
                    ]
                  : [
                      {id: 1, label: 'Program', completed: activeStep > 1, disabled: isWorking},
                      {id: 2, label: 'School', completed: activeStep > 2, disabled: isWorking || !canVisitSchool},
                      {id: 3, label: 'Account', disabled: isWorking || !canVisitAccount},
                    ]}
                onStepChange={leaveInlineResult}
              />

              {activeStep === 1 && (
                isTokenBackedProviderDomainError ? providerTokenRecovery : (
                  <SignupProgramStep
                    program={subject}
                    isWorking={isWorking}
                    isTokenVisible={isTokenVisible}
                    hasConfirmedSubscription={Boolean(preparedTokenContext)}
                    subscriptionTokenControls={subscriptionTokenControls}
                    actionError={actionError?.message || ''}
                    onProgramChange={setSubject}
                    onTokenVisibilityChange={setIsTokenVisible}
                    onContinue={() => setActiveStep(
                      !isDirectProviderContinuation && providerMappingStatus === 'exact' && campusId === providerMappedCampusId ? 3 : 2,
                    )}
                  />
                )
              )}

              {activeStep === 2 && (
                <Box component="section" aria-labelledby="school-heading" sx={signupPageStyles.stepContent()}>
                  <SignupSummaryCard label="Program" value={programName} isWorking={isWorking} onChange={() => leaveInlineResult(1)} />
                  <SignupSchoolFinder
                    value={schoolFinder}
                    onChange={setSchoolFinder}
                    onContinue={() => {
                      if (isDirectProviderContinuation) {
                        void completeProviderFromSchool();
                        return;
                      }
                      setActiveStep(3);
                    }}
                    actionError={schoolStepActionError}
                    canContinue={!isDirectProviderContinuation || Boolean(directProviderState)}
                    isCompleting={isDirectProviderContinuation && isWorking}
                  />
                </Box>
              )}

              {activeStep === 3 && (
                <Box sx={signupPageStyles.stepContent()}>
                  <Box sx={signupPageStyles.summaryStack()}><SignupSummaryCard label="Program" value={programName} isWorking={isWorking} onChange={() => leaveInlineResult(1)} /><SignupSummaryCard label="School" value={preparedTokenContext?.schoolName || schoolName} detail={preparedTokenContext ? '' : schoolDetail} isWorking={isWorking} onChange={() => leaveInlineResult(preparedTokenContext ? 1 : 2)} /></Box>
                  {accountContent}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
